import {
  ElementStatus,
  MoveEventHandler,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useLinearTransform,
  useMove,
  useUpdateBounds,
  ViewElementContainer,
  move,
  toDragPointerEvent
} from '@st/document-viewer-kit'
import {
  getCalculatorTapeLayout,
  rebuildCalculatorTape,
  type CalculatorTapeLayout,
  type CalculatorTapeSize
} from '@st/document-components/annotations'
import { Offset, Rect } from '@st/geom'
import { CalculatorTapeAnnotation, CalculatorTapeLineItem } from '@st/sdk'
import { CheckIcon, EditIcon, FontSizeLargerIcon, FontSizeSmallerIcon } from '@st/theme'
import { CalculatorExpression } from '@st/util/calculator-expression'
import { clsx } from 'clsx'
import { ReactNode, useRef, useState } from 'react'
import { DeleteIcon } from '~/components/icons'
import { DocumentAnnotationDisplay } from '~/st-doc-viewer/document-annotation-display'
import { CalculatorTapeToolState } from '~/st-doc-viewer/annotations'
import { constrainPageRectMove } from '~/st-doc-viewer/page-annotation-bounds'
import { useAnnotationsContext } from '~/st-doc-viewer/annotations-context'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '~/st-doc-viewer/floating-toolbar'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'
import { applyCalculatorTapeGridInput } from './model'

function CalculatorTapeAnnotationDisplay({ annotation }: { annotation: CalculatorTapeAnnotation }) {
  return <DocumentAnnotationDisplay annotation={annotation} />
}

function resizeCalculatorTape(
  annotation: CalculatorTapeAnnotation,
  nextSize: CalculatorTapeSize,
  pageBounds: Rect
): CalculatorTapeAnnotation {
  const resizedAnnotation = rebuildCalculatorTape({ ...annotation, size: nextSize })
  return {
    ...resizedAnnotation,
    bounds: constrainPageRectMove(resizedAnnotation.bounds, pageBounds)
  }
}

function updateDraftBounds(
  annotation: CalculatorTapeAnnotation,
  bounds: Rect,
  pageBounds: Rect,
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
) {
  send({
    type: 'updateAnnotationDraft',
    annotationDraft: {
      ...annotation,
      bounds: constrainPageRectMove(bounds, pageBounds)
    }
  })
}

function CalculatorTapeSizeButtons({
  annotation,
  page,
  send
}: {
  annotation: CalculatorTapeAnnotation
  page: PageStructure
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  const smallerSize = annotation.size == 'md' ? 'sm' : undefined
  const largerSize = annotation.size == 'sm' ? 'md' : undefined

  return (
    <>
      <FloatingToolbarButton
        ariaLabel="Shrink calculator tape"
        disabled={!smallerSize}
        icon={<FontSizeSmallerIcon className="size-6" />}
        onClick={
          smallerSize
            ? () =>
                send({
                  type: 'updateAnnotationDraft',
                  annotationDraft: resizeCalculatorTape(annotation, smallerSize, page.bounds)
                })
            : undefined
        }
      />
      <FloatingToolbarButton
        ariaLabel="Grow calculator tape"
        disabled={!largerSize}
        icon={<FontSizeLargerIcon className="size-6" />}
        onClick={
          largerSize
            ? () =>
                send({
                  type: 'updateAnnotationDraft',
                  annotationDraft: resizeCalculatorTape(annotation, largerSize, page.bounds)
                })
            : undefined
        }
      />
    </>
  )
}

export function CalculatorTapeToolOverlay({
  page,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  activeAnnotation: CalculatorTapeAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  const transform = useLinearTransform()
  const { currentUser } = useAnnotationsContext()

  return (
    <div
      style={{
        position: 'absolute',
        cursor: 'crosshair',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

        if (activeAnnotation) {
          if (status == 'editing') {
            // Don't automatically save/discard when clicking outside
            // User must explicitly save or cancel using the toolbar buttons
          } else {
            send({ type: 'deselectAnnotation' })
          }
        } else {
          // Create a new calculator tape draft at the clicked location
          const initialTape = rebuildCalculatorTape({
            type: 'calculator_tape',
            size: 'md',
            authorId: currentUser?.id,
            pageNumber: page.pageNumber,
            bounds: {
              x: pointerEvent.point.x,
              y: pointerEvent.point.y,
              width: 0,
              height: 0
            },
            title: '',
            lineItems: [],
            total: '0.00'
          })

          send({
            type: 'requestAnnotationDraft',
            elementStatus: 'editing',
            annotationDraft: initialTape
          })
        }
      }}
    />
  )
}

export function CalculatorTapeAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: CalculatorTapeAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  switch (status) {
    case 'none':
      return (
        <ViewElementContainer
          key={annotation.id}
          bounds={annotation.bounds}
          onClick={() => {
            send({ type: 'selectAnnotation', annotationId: annotation.id })
          }}
        >
          <CalculatorTapeAnnotationDisplay annotation={annotation} />
        </ViewElementContainer>
      )
    case 'selected':
      return <CalculatorTapeSelected annotation={annotation} page={page} send={send} />
    case 'editing':
      return <CalculatorTapeEditor annotation={annotation} page={page} send={send} />
  }
}

function CalculatorTapeSelected({
  annotation,
  page,
  send
}: {
  annotation: CalculatorTapeAnnotation
  page: PageStructure
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  const { bounds, onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    constrain: (event) => constrainPageRectMove(event.newBounds, page.bounds),
    onCommit: (event) => updateDraftBounds(annotation, event.newBounds, page.bounds, send)
  })

  function handleNudge(offset: Partial<Offset>) {
    updateDraftBounds(annotation, move(annotation.bounds, offset), page.bounds, send)
  }

  useKeyboardShortcuts({
    Backspace: () => {
      send({ type: 'deleteAnnotation', annotationId: annotation.id })
    },
    ArrowUp: () => handleNudge({ dy: -5 }),
    ArrowDown: () => handleNudge({ dy: 5 }),
    ArrowLeft: () => handleNudge({ dx: -5 }),
    ArrowRight: () => handleNudge({ dx: 5 })
  })

  return (
    <SelectedElementContainer
      bounds={bounds}
      onMove={onMove}
      resizable={false}
      toolbar={
        <AnnotationFloatingToolbar>
          <CalculatorTapeSizeButtons annotation={annotation} page={page} send={send} />
          <FloatingToolbarButton
            icon={<EditIcon className="size-5" />}
            onClick={() => {
              send({ type: 'beginEditingAnnotation', annotationDraft: annotation })
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <CalculatorTapeAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

/**
 * Pure layout component – takes every visual prop as an argument and
 * leaves behaviour to the caller via `renderRow`.
 */
function CalculatorTapeScaffold({
  layout,
  width,
  height,
  title,
  lineItems,
  total,
  renderRow,
  renderTitle,
  border = true
}: {
  layout: CalculatorTapeLayout
  width: number
  height: number
  title: string
  lineItems: CalculatorTapeLineItem[]
  total: string
  renderRow: (item: CalculatorTapeLineItem, index: number) => ReactNode
  renderTitle?: (title: string) => ReactNode
  border?: boolean
}) {
  const pad = layout.container.padding

  return (
    <div
      style={{
        width,
        height,
        backgroundColor: '#FFFFFF',
        border: border ? '2px solid #000000' : undefined,
        boxSizing: 'border-box',
        padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`
      }}
    >
      {/* Vertical stack */}
      <div className="flex w-full flex-col">
        {/* Title row */}
        <div style={{ height: layout.title.height }} className="flex w-full items-center">
          {renderTitle ? renderTitle(title) : null}
        </div>

        {/* Spacer before first divider */}
        <div
          style={{
            height: layout.dividerHeight
          }}
        />

        {/* Line items */}
        {lineItems.map(renderRow)}

        {/* Divider before total */}
        <HDivider height={layout.dividerHeight} />

        {/* Total row */}
        <TotalRow layout={layout} total={total} />
      </div>
    </div>
  )
}

function CalculatorTapeEditor({
  annotation,
  page,
  send
}: {
  annotation: CalculatorTapeAnnotation
  page: PageStructure
  send: (message: ToolMessage<CalculatorTapeAnnotation, CalculatorTapeToolState>) => void
}) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const layout = getCalculatorTapeLayout(annotation)
  const editorLayout = layout.editor

  const { bounds, onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    constrain: (event) => constrainPageRectMove(event.newBounds, page.bounds),
    onCommit: (event) => updateDraftBounds(annotation, event.newBounds, page.bounds, send)
  })

  function applyInputUpdate(buttonValue: string) {
    const result = applyCalculatorTapeGridInput(annotation, inputValue, buttonValue)

    if (result.annotationDraft) {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: result.annotationDraft
      })
    }

    setInputValue(result.inputValue)
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(result.inputValue.length, result.inputValue.length)
    })
  }

  return (
    <SelectedElementContainer
      bounds={bounds}
      draggable={false}
      resizable={false}
      selectionBorder={false}
      toolbar={
        <AnnotationFloatingToolbar>
          <CalculatorTapeSizeButtons annotation={annotation} page={page} send={send} />
          <FloatingToolbarButton
            icon={<CheckIcon className="size-5" />}
            onClick={() => {
              const rebuilt = rebuildCalculatorTape(annotation, {
                removeEmptyLineItems: true
              })
              if (rebuilt.lineItems.length == 0) {
                send({
                  type: 'discardAnnotationDraft',
                  annotationId: annotation.id
                })
              } else {
                send({ type: 'updateAnnotationDraft', annotationDraft: rebuilt })
                send({ type: 'deselectAnnotation' })
              }
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <div className="rounded-sm bg-white drop-shadow-lg">
        <CalculatorTapeDragHandle onMove={onMove} />

        {annotation.lineItems.length > 0 && (
          <CalculatorTapeScaffold
            layout={layout}
            width={annotation.bounds.width}
            height={annotation.bounds.height}
            title={annotation.title || ''}
            lineItems={annotation.lineItems}
            total={annotation.total}
            border={false}
            renderRow={(item, idx) => (
              <EditableLineItemRow
                key={idx}
                layout={layout}
                value={item}
                onChange={(newItem) => {
                  const newLineItems = [...annotation.lineItems]
                  newLineItems[idx] = newItem

                  send({
                    type: 'updateAnnotationDraft',
                    annotationDraft: rebuildCalculatorTape({
                      ...annotation,
                      lineItems: newLineItems
                    })
                  })
                }}
              />
            )}
            renderTitle={(currentTitle) => (
              <input
                type="text"
                value={currentTitle}
                placeholder="Title"
                className="w-full rounded-sm border-none bg-stone-100 px-0 py-0 placeholder-gray-400 outline-none focus:ring-0"
                onChange={(e) =>
                  send({
                    type: 'updateAnnotationDraft',
                    annotationDraft: rebuildCalculatorTape({ ...annotation, title: e.target.value })
                  })
                }
                style={{
                  fontSize: layout.title.fontSize,
                  fontFamily: 'Helvetica',
                  fontKerning: 'none',
                  lineHeight: 1
                }}
              />
            )}
          />
        )}
        <div
          className="flex flex-col"
          style={{
            gap: editorLayout.controls.gap,
            padding: `${editorLayout.controls.padding.top}px ${editorLayout.controls.padding.right}px ${editorLayout.controls.padding.bottom}px ${editorLayout.controls.padding.left}px`
          }}
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus={true}
            ref={inputRef}
            onKeyDown={(event) => {
              const buttonValue = getKeyDownOp(event)
              if (!buttonValue) return

              applyInputUpdate(buttonValue)
              event.preventDefault()
            }}
            className="w-full rounded-sm border-none py-0 text-right ring-1 ring-gray-400 focus:ring-gray-600"
            style={{
              height: editorLayout.input.height,
              paddingInline: editorLayout.input.paddingX,
              fontSize: editorLayout.input.fontSize,
              fontFamily: 'Helvetica',
              lineHeight: 1,
              fontKerning: 'none'
            }}
          />

          <CalculatorGrid layout={editorLayout.keypad} onButtonPress={applyInputUpdate} />
        </div>
      </div>
    </SelectedElementContainer>
  )
}

function CalculatorTapeDragHandle({ onMove }: { onMove: MoveEventHandler }) {
  const { ref, onPointerDown, onPointerMove, onPointerUp } = useMove({
    canvasClass: 'drawing-canvas',
    onMove
  })

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="relative -mt-2 h-2 cursor-move rounded-t-sm bg-gray-200"
    />
  )
}

function EditableLineItemRow({
  layout,
  value,
  onChange
}: {
  layout: CalculatorTapeLayout
  value: CalculatorTapeLineItem
  onChange: (newValue: CalculatorTapeLineItem) => void
}) {
  return (
    <div
      style={{
        height: layout.row.height,
        display: 'flex',
        alignItems: 'center',
        gap: layout.column.gap,
        fontKerning: 'none'
      }}
    >
      <div
        style={{
          flex: layout.column.leftFlex,
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <input
          type="text"
          value={value.description}
          placeholder={value.description == '' ? 'Description' : ''}
          className="my-0 w-full rounded-sm border-none bg-stone-100 px-0 py-0 placeholder-gray-400 outline-none focus:ring-0"
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          style={{
            fontSize: layout.row.fontSize,
            fontFamily: 'Helvetica',
            fontKerning: 'none',
            lineHeight: 1
          }}
        />
      </div>
      <div
        style={{
          flex: layout.column.rightFlex,
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <input
          type="text"
          value={value.amount}
          className="my-0 w-full rounded-sm border-none bg-stone-100 px-0 py-0 text-right placeholder-gray-300 outline-none focus:ring-0"
          placeholder={value.amount == '' ? 'Amount' : ''}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          onBlur={() => {
            const evaluated = CalculatorExpression.eval(value.amount)
            if (evaluated) {
              onChange({ ...value, amount: evaluated })
            }
          }}
          style={{
            fontSize: layout.row.fontSize,
            fontFamily: 'Helvetica',
            fontKerning: 'none',
            lineHeight: 1,
            color: value.amount.startsWith('-') ? '#CB3A31' : '#000000'
          }}
        />
      </div>
    </div>
  )
}

function TotalRow({ layout, total }: { layout: CalculatorTapeLayout; total: string }) {
  return (
    <div
      style={{
        height: layout.row.height,
        width: '100%'
      }}
      className="flex items-center"
    >
      <div
        className="font-bold"
        style={{
          fontSize: layout.row.fontSize,
          fontFamily: 'Helvetica',
          flex: layout.column.leftFlex,
          fontKerning: 'none'
        }}
      >
        Total
      </div>
      <div
        className="text-right font-bold"
        style={{
          fontSize: layout.row.fontSize,
          fontFamily: 'Helvetica',
          flex: layout.column.rightFlex,
          fontKerning: 'none'
        }}
      >
        {total}
      </div>
    </div>
  )
}

function HDivider({ height }: { height: number }) {
  return (
    <div className="flex flex-col" style={{ width: '100%' }}>
      <div style={{ height: height / 2 - 0.5, width: '100%' }} />
      <div style={{ height: 1, width: '100%', backgroundColor: '#000000' }} />
      <div style={{ height: height / 2 - 0.5, width: '100%' }} />
    </div>
  )
}

function CalculatorGrid({
  layout,
  onButtonPress
}: {
  layout: CalculatorTapeLayout['editor']['keypad']
  onButtonPress: (character: string) => void
}) {
  const BUTTONS_GRID = [
    ['7', '8', '9', 'x'],
    ['4', '5', '6', '÷'],
    ['1', '2', '3', '+'],
    ['0', '.', '=', '-']
  ]

  function normalizeChar(char: string): string {
    if (char === 'x') return '*'
    if (char === '÷') return '/'
    return char
  }

  return (
    <div className="grid grid-cols-4 bg-white" style={{ gap: layout.gap }}>
      {BUTTONS_GRID.flat().map((char) => {
        const isDigit = /^\d$/.test(char)
        return (
          <button
            key={char}
            onClick={() => onButtonPress(normalizeChar(char))}
            className={clsx(
              'flex items-center justify-center rounded-sm text-black hover:bg-gray-100 focus:outline-none',
              isDigit ? 'bg-gray-200 active:bg-gray-300' : 'bg-gray-300 active:bg-gray-400'
            )}
            style={{
              height: layout.buttonHeight,
              paddingInline: layout.buttonPaddingX,
              fontSize: layout.buttonFontSize,
              lineHeight: 1,
              fontKerning: 'none'
            }}
          >
            {char}
          </button>
        )
      })}
    </div>
  )
}

type KeyDownOp = '=' | '+' | '-'

function getKeyDownOp(event: {
  key: string
  code: string
  altKey: boolean
  ctrlKey: boolean
  metaKey: boolean
}): KeyDownOp | undefined {
  if (event.altKey || event.ctrlKey || event.metaKey) return undefined
  if (event.key == 'Enter') return '='
  if (event.key == '+' || event.code == 'NumpadAdd') return '+'
  if (event.key == '-' || event.code == 'NumpadSubtract') return '-'
  return undefined
}
